import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ResumePreview from '../components/ResumePreview';
import {
  Search, MapPin, Clock, Briefcase, Globe, X, ChevronLeft,
  Share2, ExternalLink, FileText, MessageSquare, Mail, SlidersHorizontal,
  Check, Loader2, AlertCircle, ChevronDown, DollarSign, Key, CheckCircle2,
  Sparkles, Bookmark, BookmarkCheck, Lock, Plus, KanbanSquare, Undo2,
} from 'lucide-react';
import { useClaudeAI } from '../hooks/useClaudeAI';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import RocketLoader from '../components/RocketLoader';
import DailyLimitModal from '../components/DailyLimitModal';
import { useTrackedJobs } from '../job-tracker/hooks/useTrackedJobs';
import { stageMeta } from '../job-tracker/constants';

const BASE = process.env.REACT_APP_API_URL || '/api';

/* ── JSON helpers for AI responses ─────────────────────────────────────────── */
const _stripFences = (r) => r.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();
const _parseJSON = (raw) => {
  const c = _stripFences(raw);
  try { return JSON.parse(c); } catch (_) {}
  const m = c.match(/(\{[\s\S]*\})/);
  if (m) { try { return JSON.parse(m[1]); } catch (_) {} }
  throw new Error('Could not parse AI response');
};

const searchJobs = async ({ query = '', page = 1, country = '', city = '', jobType = '', remote = false, datePosted = 'all', signal } = {}) => {
  const params = new URLSearchParams({
    query, page, country, city, jobType,
    remote: remote ? 'true' : 'false',
    datePosted,
  });
  const token = localStorage.getItem('rb_token');
  const res = await fetch(`${BASE}/jobs/search?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  const data = await res.json();
  if (!res.ok) throw Object.assign(new Error(data.error || 'Request failed'), data);
  return data;
};

/* Coerce a provider highlight list into renderable strings. */
const toHighlightText = (arr) => (Array.isArray(arr) ? arr : [])
  .map((v) => {
    if (typeof v === 'string') return v.trim();
    if (v && typeof v === 'object') return String(v.label || v.name || v.text || v.title || '').trim();
    return v == null ? '' : String(v).trim();
  })
  .filter(Boolean);

/* ── Countries list ─────────────────────────────────────────────────────── */
const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Australia','Austria','Bangladesh',
  'Belgium','Brazil','Canada','Chile','China','Colombia','Croatia','Czech Republic',
  'Denmark','Egypt','Estonia','Ethiopia','Finland','France','Germany','Ghana','Greece',
  'Hungary','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Japan',
  'Jordan','Kenya','Malaysia','Mexico','Morocco','Netherlands','New Zealand','Nigeria',
  'Norway','Pakistan','Philippines','Poland','Portugal','Romania','Russia','Saudi Arabia',
  'Singapore','South Africa','South Korea','Spain','Sri Lanka','Sweden','Switzerland',
  'Taiwan','Thailand','Turkey','Ukraine','United Arab Emirates','United Kingdom',
  'United States','Vietnam','Anywhere','Remote',
].sort();

/* ── Helpers ────────────────────────────────────────────────────────────── */
const timeAgo = (ts) => {
  if (!ts) return 'Recently';
  const diff = Date.now() - ts * 1000;
  const h = Math.floor(diff / 3600000);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return `${Math.floor(d / 7)}w ago`;
};

const TAG_COLORS = {
  'fulltime':    { bg: '#F0FDF4', color: '#15803D' },
  'full-time':   { bg: '#F0FDF4', color: '#15803D' },
  'Full-time':   { bg: '#F0FDF4', color: '#15803D' },
  'parttime':    { bg: '#FFF7ED', color: '#C2410C' },
  'part-time':   { bg: '#FFF7ED', color: '#C2410C' },
  'Part-time':   { bg: '#FFF7ED', color: '#C2410C' },
  'contractor':  { bg: '#FAF5FF', color: '#7E22CE' },
  'contract':    { bg: '#FAF5FF', color: '#7E22CE' },
  'Contract':    { bg: '#FAF5FF', color: '#7E22CE' },
  'intern':      { bg: '#FFF1F2', color: '#BE123C' },
  'internship':  { bg: '#FFF1F2', color: '#BE123C' },
  'Internship':  { bg: '#FFF1F2', color: '#BE123C' },
  'remote':      { bg: '#EFF6FF', color: '#1D4ED8' },
  'Remote':      { bg: '#EFF6FF', color: '#1D4ED8' },
  'default':     { bg: '#F1F5F9', color: '#475569' },
};

const Tag = ({ label }) => {
  const style = TAG_COLORS[label?.toLowerCase()] || TAG_COLORS.default;
  return (
    <span style={{ ...style, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', display: 'inline-block' }}>
      {label}
    </span>
  );
};

/* ── Company Avatar ─────────────────────────────────────────────────────── */
const CompanyAvatar = ({ name, logo, size = 40 }) => {
  const [err, setErr] = useState(false);
  const letter = (name || '?').charAt(0).toUpperCase();
  const palette = ['#EF1D28','#1D4ED8','#059669','#D97706','#EF1D28','#0891B2','#DC2626','#0D9488'];
  const bg = palette[letter.charCodeAt(0) % palette.length];

  if (logo && !err) {
    return (
      <img src={logo} alt={name} onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: 8, objectFit: 'contain', border: '1px solid #E5E7EB', background: 'white', padding: 4, flexShrink: 0 }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: 8, background: bg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: Math.round(size * 0.4), flexShrink: 0, fontFamily: 'Inter,sans-serif' }}>
      {letter}
    </div>
  );
};

/* ── Country Dropdown ───────────────────────────────────────────────────── */
const CountryDropdown = ({ value, onChange, dark = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const filtered = COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()));
  const hasVal = !!value;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const btnStyle = dark
    ? { padding: '6px 12px', border: `1.5px solid ${hasVal ? '#FB923C' : 'rgba(255,255,255,0.18)'}`, borderRadius: 20, background: hasVal ? 'rgba(251,146,60,0.15)' : 'rgba(255,255,255,0.08)', color: hasVal ? '#FB923C' : 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: hasVal ? 600 : 400, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 5 }
    : { padding: '6px 12px', border: `1.5px solid ${hasVal ? '#EF1D28' : '#E5E7EB'}`, borderRadius: 20, background: hasVal ? '#FFF1F2' : 'white', color: hasVal ? '#EF1D28' : '#374151', fontSize: 12, fontWeight: hasVal ? 600 : 400, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 5 };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(p => !p)} style={btnStyle}>
        <Globe size={11} />
        {hasVal ? value : 'Any Country'}
        {hasVal
          ? <X size={10} onClick={e => { e.stopPropagation(); onChange(''); setOpen(false); }} />
          : <ChevronDown size={10} />}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 9999, background: 'white', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', width: 230, overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FAFC', borderRadius: 7, padding: '5px 8px' }}>
              <Search size={12} color="#9CA3AF" />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search country…"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
              {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 0 }}><X size={11} /></button>}
            </div>
          </div>
          <div style={{ maxHeight: 230, overflowY: 'auto' }}>
            <CountryOption label="Any Country" selected={!value} onSelect={() => { onChange(''); setOpen(false); setSearch(''); }} />
            {filtered.map(c => (
              <CountryOption key={c} label={c} selected={value === c} onSelect={() => { onChange(c); setOpen(false); setSearch(''); }} />
            ))}
            {filtered.length === 0 && <div style={{ padding: 12, fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No results</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const CountryOption = ({ label, selected, onSelect }) => (
  <button onClick={onSelect}
    style={{ width: '100%', padding: '7px 12px', border: 'none', background: selected ? '#FFF1F2' : 'transparent', textAlign: 'left', fontSize: 12, color: selected ? '#EF1D28' : '#374151', cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'flex', alignItems: 'center', gap: 6, fontWeight: selected ? 600 : 400 }}
    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#F8FAFC'; }}
    onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
    {selected && <Check size={10} />}{label}
  </button>
);

/* ── Filter Panel ───────────────────────────────────────────────────────── */
const FILTER_JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship'];
const FILTER_DATE       = [
  { label: 'Any time',   val: 'all' },
  { label: 'Past 24h',   val: 'today' },
  { label: 'Past 3 days',val: '3days' },
  { label: 'Past week',  val: 'week' },
  { label: 'Past month', val: 'month' },
];

const FilterChip = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: '5px 11px', borderRadius: 6, border: `1px solid ${active ? '#EF1D28' : '#E5E7EB'}`,
    background: active ? '#FFF1F2' : 'white', color: active ? '#EF1D28' : '#374151',
    fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'Inter,sans-serif',
    display: 'inline-flex', alignItems: 'center', gap: 4,
  }}>
    {active && <Check size={10} />}{label}
  </button>
);

const FSec = ({ label, icon, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>{icon}{label}</div>
    {children}
  </div>
);

const FilterPanel = ({ filters, setFilters, country, setCountry, city, setCity, onClose, onApply }) => {
  const [cs, setCs]         = useState('');
  const [compVal, setCompVal] = useState('');
  const fcountries = COUNTRIES.filter(c => c.toLowerCase().includes(cs.toLowerCase()));

  const toggle = (key, val) => setFilters(prev => {
    const arr = prev[key] || [];
    return { ...prev, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
  });

  const Section = ({ title, icon, children }) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {icon}{title}
      </div>
      {children}
    </div>
  );

  return createPortal(
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }} />

      {/* Centered modal */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <div style={{
          width: '100%', maxWidth: 620, maxHeight: '88vh',
          background: 'white', borderRadius: 20,
          boxShadow: '0 24px 80px rgba(0,0,0,0.28)',
          display: 'flex', flexDirection: 'column',
          fontFamily: 'Inter,sans-serif', pointerEvents: 'all',
          animation: 'ddFadeIn 0.2s ease',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #F1F5F9', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 800, fontSize: 17, color: '#111827' }}>All Filters</span>
              {[(country||city),(filters.jobTypes||[]).length,(filters.datePosted&&filters.datePosted!=='all'?1:0),(filters.salary?1:0)].flat().filter(Boolean).length > 0 && (
                <span style={{ background: '#EF1D28', color: 'white', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 8px' }}>
                  {[(country||city)?1:0,(filters.jobTypes||[]).length,(filters.datePosted&&filters.datePosted!=='all'?1:0),(filters.salary?1:0)].reduce((a,b)=>a+b,0)} active
                </span>
              )}
            </div>
            <button onClick={onClose} style={{ background: '#F3F4F6', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 7, borderRadius: 8, display: 'flex' }}>
              <X size={16} />
            </button>
          </div>

          {/* Scrollable body — 2-column grid */}
          <div style={{ overflowY: 'auto', padding: '24px 24px 0', flex: 1 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>

              {/* ── Location ── */}
              <Section title="Location" icon={<MapPin size={13} />}>
                <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '7px 10px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Search size={11} color="#9CA3AF" />
                    <input value={cs} onChange={e => setCs(e.target.value)} placeholder="Search country…"
                      style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
                    {cs && <button onClick={() => setCs('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#9CA3AF' }}><X size={11} /></button>}
                  </div>
                  <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                    {[{ label: 'Any Country', val: '' }, ...fcountries.map(c => ({ label: c, val: c }))].map(({ label, val }) => (
                      <button key={label} onClick={() => setCountry(val)}
                        style={{ width: '100%', padding: '7px 12px', border: 'none', background: country === val ? '#FFF1F2' : 'transparent', textAlign: 'left', fontSize: 12, color: country === val ? '#EF1D28' : '#374151', cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'flex', alignItems: 'center', gap: 6, fontWeight: country === val ? 600 : 400 }}>
                        {country === val && <Check size={10} />}{label}
                      </button>
                    ))}
                    {fcountries.length === 0 && <div style={{ padding: 12, fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No results</div>}
                  </div>
                  <div style={{ padding: '7px 10px', borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MapPin size={11} color="#9CA3AF" />
                    <input value={city} onChange={e => setCity(e.target.value)} placeholder="City (e.g. New York)…"
                      style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
                    {city && <button onClick={() => setCity('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#9CA3AF' }}><X size={11} /></button>}
                  </div>
                </div>
              </Section>

              {/* ── Job Type ── */}
              <Section title="Job Type" icon={<Briefcase size={13} />}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {FILTER_JOB_TYPES.map(t => (
                    <FilterChip key={t} label={t} active={(filters.jobTypes||[]).includes(t)} onClick={() => toggle('jobTypes', t)} />
                  ))}
                </div>
              </Section>

              {/* ── Salary ── */}
              <Section title="Salary" icon={<DollarSign size={13} />}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {SALARY_OPTIONS.map(({ label, val }) => (
                    <FilterChip key={val} label={label} active={(filters.salary||'') === val} onClick={() => setFilters(p => ({ ...p, salary: val }))} />
                  ))}
                </div>
              </Section>

              {/* ── Date Posted ── */}
              <Section title="Date Posted" icon={<Clock size={13} />}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {FILTER_DATE.map(d => (
                    <FilterChip key={d.val} label={d.label} active={(filters.datePosted||'all') === d.val} onClick={() => setFilters(p => ({ ...p, datePosted: d.val }))} />
                  ))}
                </div>
              </Section>

              {/* ── Companies ── */}
              <Section title="Companies" icon={<Globe size={13} />}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
                  <Search size={11} color="#9CA3AF" />
                  <input value={compVal} onChange={e => setCompVal(e.target.value)} placeholder="e.g. Google, Amazon…"
                    style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {POPULAR_COMPANIES.map(c => (
                    <button key={c.name} onClick={() => setCompVal(c.name)}
                      style={{ padding: '4px 10px', border: `1.5px solid ${compVal===c.name ? c.color : c.color+'33'}`, borderRadius: 20, background: compVal===c.name ? c.color+'22' : c.color+'11', color: c.color, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </Section>

              {/* ── Keywords ── */}
              <Section title="Keywords" icon={<Search size={13} />}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
                  <Search size={11} color="#9CA3AF" />
                  <input placeholder="e.g. Python, React, AWS…"
                    style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {TRENDING_SKILLS.slice(0,6).map(s => (
                    <span key={s} style={{ padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: 20, background: '#F8FAFC', color: '#374151', fontSize: 11, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>{s}</span>
                  ))}
                </div>
              </Section>

            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', gap: 10, padding: '18px 24px', borderTop: '1px solid #F1F5F9', flexShrink: 0 }}>
            <button onClick={() => { setFilters({}); setCountry(''); setCity(''); setCs(''); setCompVal(''); }}
              style={{ flex: 1, padding: '11px 0', border: '1.5px solid #E5E7EB', borderRadius: 10, background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151', fontFamily: 'Inter,sans-serif', fontWeight: 500 }}>
              Reset All
            </button>
            <button onClick={() => { onApply(); if (compVal) {} }}
              style={{ flex: 2, padding: '11px 0', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#EF1D28,#C5161D)', fontSize: 14, cursor: 'pointer', color: 'white', fontWeight: 700, fontFamily: 'Inter,sans-serif', boxShadow: '0 3px 12px rgba(239,29,40,0.3)' }}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

/* ── Popular companies & trending ───────────────────────────────────────── */
const POPULAR_COMPANIES = [
  { name: 'Google',    color: '#4285F4' }, { name: 'Microsoft', color: '#00A4EF' },
  { name: 'Apple',     color: '#555555' }, { name: 'Amazon',    color: '#FF9900' },
  { name: 'Meta',      color: '#1877F2' }, { name: 'Netflix',   color: '#E50914' },
  { name: 'Spotify',   color: '#1DB954' },
];
const TRENDING = [
  'Software Engineer','Product Manager','Data Analyst','UX Designer',
  'DevOps Engineer','Business Analyst','Frontend Developer','AI Engineer',
];

/* ── Generic Pill Dropdown ───────────────────────────────────────────────── */
const PillDropdown = ({ label, active, children, width = 280 }) => {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      const panels = document.querySelectorAll('.pill-dd-panel');
      for (const p of panels) { if (p.contains(e.target)) return; }
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - width - 12) });
    }
    setOpen(p => !p);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button ref={btnRef} onClick={toggle} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 13px', border: `1.5px solid ${active ? '#EF1D28' : open ? '#94A3B8' : '#D1D5DB'}`,
        borderRadius: 20, background: active ? '#FFF1F2' : open ? '#F8FAFC' : 'white',
        color: active ? '#EF1D28' : '#374151', fontSize: 12,
        fontWeight: active ? 600 : 400, cursor: 'pointer',
        fontFamily: 'Inter,sans-serif', whiteSpace: 'nowrap', transition: 'all 0.15s',
      }}>
        {label}
        <ChevronDown size={10} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && createPortal(
        <div className="pill-dd-panel" style={{
          position: 'fixed', top: pos.top, left: pos.left, width,
          background: 'white', border: '1px solid #E5E7EB',
          borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          zIndex: 10001, overflow: 'hidden', fontFamily: 'Inter,sans-serif',
        }}>
          {children({ close: () => setOpen(false) })}
        </div>,
        document.body
      )}
    </div>
  );
};

/* ── Location dropdown content ───────────────────────────────────────────── */
const LocationContent = ({ current, onApply }) => {
  const initCountry = (typeof current === 'object' ? current?.country : current) || '';
  const initCity    = (typeof current === 'object' ? current?.city    : '')      || '';
  const [search,       setSearch]       = useState('');
  const [localCountry, setLocalCountry] = useState(initCountry);
  const [localCity,    setLocalCity]    = useState(initCity);
  const filtered = COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()));
  return (
    <div>
      <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Country</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px' }}>
          <Search size={12} color="#9CA3AF" />
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search country…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#9CA3AF' }}><X size={11} /></button>}
        </div>
      </div>
      <div style={{ maxHeight: 150, overflowY: 'auto' }}>
        {[{ label: 'Any Country', val: '' }, ...filtered.map(c => ({ label: c, val: c }))].map(({ label, val }) => (
          <button key={label} onClick={() => setLocalCountry(val)}
            style={{ width: '100%', padding: '7px 14px', border: 'none', background: localCountry === val ? '#FFF1F2' : 'transparent', textAlign: 'left', fontSize: 12, color: localCountry === val ? '#EF1D28' : '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: localCountry === val ? 600 : 400, fontFamily: 'Inter,sans-serif' }}>
            {localCountry === val && <Check size={10} />}{label}
          </button>
        ))}
        {filtered.length === 0 && <div style={{ padding: 14, fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No results</div>}
      </div>
      <div style={{ padding: '10px 14px', borderTop: '1px solid #F1F5F9', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>City</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px' }}>
          <MapPin size={12} color="#9CA3AF" />
          <input value={localCity} onChange={e => setLocalCity(e.target.value)} placeholder="e.g. New York, London…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
          {localCity && <button onClick={() => setLocalCity('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#9CA3AF' }}><X size={11} /></button>}
        </div>
      </div>
      <div style={{ padding: '10px 14px', display: 'flex', gap: 8 }}>
        <button onClick={() => onApply({ country: '', city: '' })} style={{ flex: 1, padding: '7px 0', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', fontSize: 12, color: '#374151', cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>Clear</button>
        <button onClick={() => onApply({ country: localCountry, city: localCity })} style={{ flex: 2, padding: '7px 0', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#EF1D28,#C5161D)', fontSize: 12, color: 'white', fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>Apply</button>
      </div>
    </div>
  );
};

/* ── Type dropdown content ───────────────────────────────────────────────── */
const TypeContent = ({ current, onApply }) => {
  const [local, setLocal] = useState(current || []);
  const toggle = (t) => setLocal(p => p.includes(t) ? p.filter(v => v !== t) : [...p, t]);
  return (
    <div>
      <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Job Type</div>
      </div>
      <div style={{ padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {FILTER_JOB_TYPES.map(t => {
          const on = local.includes(t);
          return (
            <button key={t} onClick={() => toggle(t)} style={{ padding: '7px 14px', border: `1.5px solid ${on ? '#EF1D28' : '#E5E7EB'}`, borderRadius: 20, background: on ? '#FFF1F2' : 'white', color: on ? '#EF1D28' : '#374151', fontSize: 12, fontWeight: on ? 600 : 400, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {on && <Check size={10} />}{t}
            </button>
          );
        })}
      </div>
      <div style={{ padding: '8px 14px 12px', borderTop: '1px solid #F1F5F9', display: 'flex', gap: 8 }}>
        <button onClick={() => onApply([])} style={{ flex: 1, padding: '7px 0', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', fontSize: 12, color: '#374151', cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>Clear</button>
        <button onClick={() => onApply(local)} style={{ flex: 2, padding: '7px 0', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#EF1D28,#C5161D)', fontSize: 12, color: 'white', fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>Apply</button>
      </div>
    </div>
  );
};

/* ── Salary dropdown content ─────────────────────────────────────────────── */
const SALARY_OPTIONS = [
  { label: 'Any Salary',    val: '' },
  { label: 'Below $30,000', val: 'below30k' },
  { label: '$30,000+',      val: '30k' },
  { label: '$50,000+',      val: '50k' },
  { label: '$80,000+',      val: '80k' },
  { label: '$100,000+',     val: '100k' },
  { label: '$150,000+',     val: '150k' },
];
const SalaryContent = ({ current, onApply }) => {
  const [local, setLocal] = useState(current || '');
  return (
    <div>
      <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Minimum Salary</div>
      </div>
      <div style={{ padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {SALARY_OPTIONS.map(({ label, val }) => {
          const on = local === val;
          return (
            <button key={val} onClick={() => { setLocal(val); onApply(val); }}
              style={{ padding: '8px 12px', border: 'none', background: on ? '#FFF1F2' : 'transparent', borderRadius: 8, textAlign: 'left', fontSize: 13, color: on ? '#EF1D28' : '#374151', cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'flex', alignItems: 'center', gap: 8, fontWeight: on ? 600 : 400 }}>
              {on && <Check size={11} />}{label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ── Date posted dropdown content ────────────────────────────────────────── */
const DateContent = ({ current, onApply }) => (
  <div>
    <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F1F5F9' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>Date Posted</div>
    </div>
    <div style={{ padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {FILTER_DATE.map(({ label, val }) => {
        const on = (current || 'all') === val;
        return (
          <button key={val} onClick={() => onApply(val)}
            style={{ padding: '8px 12px', border: 'none', background: on ? '#FFF1F2' : 'transparent', borderRadius: 8, textAlign: 'left', fontSize: 13, color: on ? '#EF1D28' : '#374151', cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'flex', alignItems: 'center', gap: 8, fontWeight: on ? 600 : 400 }}>
            {on && <Check size={11} />}{label}
          </button>
        );
      })}
    </div>
  </div>
);

/* ── Companies dropdown content ──────────────────────────────────────────── */
const CompaniesContent = ({ onSearch }) => {
  const [val, setVal] = useState('');
  return (
    <div>
      <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Filter by Company</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px' }}>
          <Search size={12} color="#9CA3AF" />
          <input autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && val && onSearch(val)}
            placeholder="e.g. Google, Amazon…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
        </div>
      </div>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 7, fontWeight: 500 }}>Popular</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {POPULAR_COMPANIES.map(c => (
            <button key={c.name} onClick={() => onSearch(c.name)}
              style={{ padding: '4px 11px', border: `1.5px solid ${c.color}33`, borderRadius: 20, background: `${c.color}11`, color: c.color, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div style={{ padding: '10px 14px' }}>
        <button onClick={() => val && onSearch(val)} style={{ width: '100%', padding: '8px 0', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#EF1D28,#C5161D)', fontSize: 12, color: 'white', fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', opacity: val ? 1 : 0.5 }}>Search</button>
      </div>
    </div>
  );
};

/* ── Keywords dropdown content ───────────────────────────────────────────── */
const TRENDING_SKILLS = ['Python','React','AWS','Machine Learning','SQL','TypeScript','Docker','Node.js','Figma','Product Management'];
const KeywordsContent = ({ onSearch }) => {
  const [val, setVal] = useState('');
  return (
    <div>
      <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Add Keywords</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px' }}>
          <Search size={12} color="#9CA3AF" />
          <input autoFocus value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && val && onSearch(val)}
            placeholder="e.g. Python, React, AWS…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, color: '#374151', width: '100%', fontFamily: 'Inter,sans-serif' }} />
        </div>
      </div>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #F1F5F9' }}>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 7, fontWeight: 500 }}>Trending Skills</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {TRENDING_SKILLS.map(s => (
            <button key={s} onClick={() => onSearch(s)}
              style={{ padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: 20, background: '#F8FAFC', color: '#374151', fontSize: 11, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div style={{ padding: '10px 14px' }}>
        <button onClick={() => val && onSearch(val)} style={{ width: '100%', padding: '8px 0', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#EF1D28,#C5161D)', fontSize: 12, color: 'white', fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', opacity: val ? 1 : 0.5 }}>Apply</button>
      </div>
    </div>
  );
};


/* ── Job Card ───────────────────────────────────────────────────────────── */
/* ── Add-to-Job-Tracker control ──────────────────────────────────────────────
   Separate from the bookmark icon on purpose: the bookmark is the private,
   local "Saved Jobs" list, while this puts a real card on the Job Tracker
   board. Once tracked it reports the card's live stage. */
const TrackJobButton = ({ card, busy, locked, onTrack, onUntrack, compact = false }) => {
  // Free plan: the board is Pro-only, so the control is simply absent.
  if (locked) return null;
  const tracked = !!card;
  const meta = tracked ? stageMeta(card.stage) : null;
  const label = busy ? 'Saving…' : tracked ? meta.label : 'Track';
  const Icon = busy ? Loader2 : tracked ? meta.icon : Plus;

  const click = (e) => {
    e.stopPropagation();
    if (busy) return;
    tracked ? onUntrack?.() : onTrack?.();
  };

  const base = {
    display: 'inline-flex', alignItems: 'center', gap: compact ? 4 : 7,
    cursor: busy ? 'default' : 'pointer', fontFamily: 'Inter,sans-serif',
    fontWeight: compact ? 600 : 500, transition: 'all 0.15s',
    background: tracked ? meta.bg : 'white',
    color: tracked ? meta.color : '#374151',
    border: `1px solid ${tracked ? meta.color + '55' : '#D1D5DB'}`,
    opacity: busy ? 0.7 : 1,
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      title={tracked
        ? `In your Job Tracker · ${meta.label} — click to remove`
        : 'Add this job to your Job Tracker'}
      style={compact
        ? { ...base, fontSize: 11, borderRadius: 4, padding: '2px 8px' }
        : { ...base, fontSize: 14, borderRadius: 8, padding: '10px 20px', fontWeight: tracked ? 700 : 500 }}>
      <Icon
        size={compact ? 9 : 14}
        style={busy ? { animation: 'spin 0.8s linear infinite' } : undefined} />
      {label}
    </button>
  );
};

const JobCard = ({ job, selected, onClick, saved, onToggleSave, trackCard, trackBusy, trackLocked, onTrack, onUntrack }) => (
  <div onClick={onClick} style={{
    padding: '14px 18px',
    background: selected ? '#FFF1F2' : 'white',
    cursor: 'pointer',
    borderLeft: `3px solid ${selected ? '#EF1D28' : 'transparent'}`,
    borderBottom: '1px solid #F0F2F5',
    transition: 'background 0.12s',
    position: 'relative',
  }}
    onMouseEnter={e => { if (!selected) e.currentTarget.style.background = '#F9FAFB'; }}
    onMouseLeave={e => { if (!selected) e.currentTarget.style.background = selected ? '#FFF1F2' : 'white'; }}>
    {/* Bookmark button */}
    <button
      onClick={e => { e.stopPropagation(); onToggleSave?.(job); }}
      title={saved ? 'Remove from saved' : 'Save job'}
      style={{
        position: 'absolute', top: 10, right: 12,
        background: 'none', border: 'none', cursor: 'pointer', padding: 4,
        color: saved ? '#EF1D28' : '#D1D5DB',
        display: 'flex', alignItems: 'center', lineHeight: 1,
        transition: 'color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.color = '#EF1D28'; }}
      onMouseLeave={e => { e.currentTarget.style.color = saved ? '#EF1D28' : '#D1D5DB'; }}>
      {saved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
    </button>

    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingRight: 24 }}>
      <CompanyAvatar name={job.company_name} logo={job.logo} size={44} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 2, lineHeight: 1.3 }}>{job.title}</div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden' }}>
          <span style={{ flexShrink: 0 }}>{job.company_name}</span>
          {job.location && (
            <><span style={{ margin: '0 3px', color: '#D1D5DB' }}>–</span><MapPin size={9} style={{ flexShrink: 0 }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.location}</span></>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {(job.job_types || []).slice(0, 1).map(t => (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#374151', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 4, padding: '2px 8px' }}>
              <Briefcase size={9} />{t}
            </span>
          ))}
          {job.salary && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#374151', background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 4, padding: '2px 8px' }}>
              <DollarSign size={9} />{job.salary}
            </span>
          )}
          <TrackJobButton card={trackCard} busy={trackBusy} locked={trackLocked} onTrack={onTrack} onUntrack={onUntrack} compact />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#EF4444', marginLeft: 'auto', fontWeight: 500, flexShrink: 0 }}>
            🔥 {timeAgo(job.created_at)}
          </span>
        </div>
      </div>
    </div>
  </div>
);

/* ── Parse plain-text description into structured blocks ───────────────── */
const parseDescription = (text) => {
  if (!text) return [];
  const lines = text.split(/\n+/).map(l => l.trim()).filter(Boolean);
  const blocks = [];
  let currentPara = [];

  const flushPara = () => {
    if (currentPara.length) { blocks.push({ type: 'para', lines: [...currentPara] }); currentPara = []; }
  };

  for (const line of lines) {
    const isBullet = /^[•\-\*•●]/.test(line) || /^\d+[\.\)]\s/.test(line);
    const isHeading = /^[A-Z][A-Z\s&/,]{3,}:?\s*$/.test(line) && line.length < 60;

    if (isHeading) {
      flushPara();
      blocks.push({ type: 'heading', text: line.replace(/:$/, '') });
    } else if (isBullet) {
      flushPara();
      const clean = line.replace(/^[•\-\*•●\d\.]+[\.\)?\s]*/,'').trim();
      if (blocks.length && blocks[blocks.length - 1].type === 'bullets') {
        blocks[blocks.length - 1].items.push(clean);
      } else {
        blocks.push({ type: 'bullets', items: [clean] });
      }
    } else {
      currentPara.push(line);
    }
  }
  flushPara();
  return blocks;
};

/* ── Job Detail ─────────────────────────────────────────────────────────── */
const JobDetail = ({ job, onTailorResume, onPracticeInterview, onWriteCoverLetter, onBuildResume, saved, onToggleSave, trackCard, trackBusy, trackLocked, onTrack, onUntrack, onApply }) => {
  const navigate = useNavigate();
  const descBlocks = parseDescription(job.description);
  /* Highlight entries must be plain strings — some providers return objects
     (e.g. Indeed's { label, requirementSeverity }), and rendering one directly
     throws "Objects are not valid as a React child" and blanks the page. */
  const hiQuals = toHighlightText(job.highlights?.Qualifications);
  const hiResps = toHighlightText(job.highlights?.Responsibilities);
  const hiBenef = toHighlightText(job.highlights?.Benefits);

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* Main content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px', minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
          <CompanyAvatar name={job.company_name} logo={job.logo} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: '0 0 5px', fontSize: 21, fontWeight: 700, color: '#111827', lineHeight: 1.25 }}>{job.title}</h2>
            <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>{job.company_name}</span>
              {job.location && (
                <><span style={{ color: '#D1D5DB', margin: '0 2px' }}>–</span><MapPin size={11} /><span>{job.location}</span></>
              )}
            </div>
          </div>
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          {(job.job_types || []).map(t => (
            <span key={t} style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 6, padding: '5px 13px', fontSize: 13, color: '#374151', fontWeight: 500 }}>{t}</span>
          ))}
          {job.remote && (
            <span style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: '5px 13px', fontSize: 13, color: '#15803D', fontWeight: 500 }}>Remote</span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 6, padding: '5px 13px', fontSize: 13, color: '#374151' }}>
            <Clock size={12} />{timeAgo(job.created_at)}
          </span>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 28, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Opens the listing and optimistically records it as applied — we
              cannot observe the external site, so the toast offers an undo. */}
          <a href={job.url} target="_blank" rel="noopener noreferrer"
            onClick={() => onApply?.(job)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', borderRadius: 8,
              padding: '10px 24px', fontSize: 14, fontWeight: 600, textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(239,29,40,0.3)',
            }}>
            <ExternalLink size={14} />Apply Now
          </a>
          <button onClick={() => onToggleSave?.(job)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: saved ? '#FFF1F2' : 'white',
            color: saved ? '#EF1D28' : '#374151',
            border: `1.5px solid ${saved ? '#FECACA' : '#D1D5DB'}`,
            borderRadius: 8, padding: '10px 20px', fontSize: 14,
            fontWeight: saved ? 700 : 500, cursor: 'pointer',
            fontFamily: 'Inter,sans-serif', transition: 'all 0.2s',
          }}>
            {saved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
            {saved ? 'Saved' : 'Save Job'}
          </button>
          <TrackJobButton card={trackCard} busy={trackBusy} locked={trackLocked} onTrack={onTrack} onUntrack={onUntrack} />
        </div>

        {/* Highlights sections */}
        {hiResps.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>Responsibilities</h3>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {hiResps.map((item, i) => (
                <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 5 }}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {hiQuals.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 10px' }}>Qualifications</h3>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {hiQuals.map((item, i) => (
                <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 5 }}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Full description */}
        {descBlocks.length > 0 && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #F0F2F5' }}>Job description</div>
            {descBlocks.map((block, i) => {
              if (block.type === 'heading') return (
                <h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: i > 0 ? '20px 0 8px' : '0 0 8px' }}>{block.text}</h3>
              );
              if (block.type === 'bullets') return (
                <ul key={i} style={{ margin: '0 0 12px', paddingLeft: 20 }}>
                  {block.items.map((item, j) => (
                    <li key={j} style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 4 }}>{item}</li>
                  ))}
                </ul>
              );
              return <p key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.75, margin: '0 0 12px' }}>{block.lines.join(' ')}</p>;
            })}
          </div>
        )}
      </div>

      {/* Action sidebar */}
      <div style={{ width: 220, flexShrink: 0, borderLeft: '1px solid #E5E7EB', padding: '20px 14px', overflowY: 'auto', background: '#FAFAFA' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>AI Tools for this Job</div>
        <PrepCard
          gradient="linear-gradient(135deg,#EF1D28 0%,#C5161D 100%)"
          iconBg="rgba(255,255,255,0.2)"
          icon={<FileText size={18} color="white" />}
          badge="AI Powered"
          title="Tailor Resume"
          desc="Instantly adapt your existing resume to match this job's keywords and requirements."
          btnLabel="Tailor My Resume →"
          onAction={onTailorResume}
        />
        <PrepCard
          gradient="linear-gradient(135deg,#0D1117 0%,#1E2A3A 100%)"
          iconBg="rgba(239,29,40,0.3)"
          icon={<MessageSquare size={18} color="#FF6B6B" />}
          badge="AI Powered"
          title="Practice Interview"
          desc="Prepare for this role with AI-generated interview questions tailored to the job."
          btnLabel="Start Practice →"
          onAction={onPracticeInterview}
        />
        <PrepCard
          gradient="linear-gradient(135deg,#14532D 0%,#16A34A 100%)"
          iconBg="rgba(255,255,255,0.18)"
          icon={<Mail size={18} color="white" />}
          badge="Cover Letter"
          title="Write Cover Letter"
          desc="Generate a personalized cover letter for this position in seconds."
          btnLabel="Write Cover Letter →"
          onAction={onWriteCoverLetter}
          last
        />
      </div>
    </div>
  );
};

const PrepCard = ({ gradient, iconBg, icon, badge, title, desc, btnLabel, onAction, last }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ marginBottom: last ? 0 : 8, borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>
      {/* Header row — always visible, click to toggle */}
      <button
        onClick={() => setOpen(p => !p)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          background: gradient, border: 'none', cursor: 'pointer',
          padding: '11px 13px', textAlign: 'left',
        }}
      >
        <div style={{ width: 30, height: 30, borderRadius: 8, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </div>
        <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'white', lineHeight: 1.3 }}>{title}</span>
        <ChevronDown size={14} color="rgba(255,255,255,0.75)"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>

      {/* Expandable body */}
      {open && (
        <div style={{ background: gradient, borderTop: '1px solid rgba(255,255,255,0.12)', padding: '0 13px 13px' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.82)', lineHeight: 1.55, marginBottom: 11, paddingTop: 10 }}>{desc}</div>
          <button
            onClick={onAction}
            style={{
              width: '100%', padding: '8px 0', border: 'none',
              borderRadius: 8, background: 'rgba(255,255,255,0.95)',
              color: '#111827', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'Inter,sans-serif',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'white'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.95)'}
          >
            {btnLabel}
          </button>
        </div>
      )}
    </div>
  );
};

/* ── API key missing notice ──────────────────────────────────────────────── */
const ApiKeyNotice = () => (
  <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: 20, fontFamily: 'Inter,sans-serif', marginBottom: 24 }}>
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <Key size={18} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#92400E', marginBottom: 6 }}>Job search API key not configured</div>
        <div style={{ fontSize: 12.5, color: '#78350F', lineHeight: 1.6, marginBottom: 10 }}>
          Job Search uses <strong>Indeed Scraper by RapidAPI</strong> to fetch live jobs from Indeed worldwide.
        </div>
        <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#78350F', lineHeight: 1.8 }}>
          <li>Open <strong>server/.env</strong></li>
          <li>Set <code style={{ background: '#FEF3C7', padding: '1px 4px', borderRadius: 3 }}>RAPIDAPI_KEY=your_key_here</code></li>
          <li>Restart the server — jobs will load automatically</li>
        </ol>
      </div>
    </div>
  </div>
);

/* ── Free-plan monthly search limit notice ──────────────────────────────── */
const SearchLimitNotice = ({ limit, onUpgrade }) => (
  <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '16px 18px', fontFamily: 'Inter,sans-serif', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
    <div style={{ width: 34, height: 34, borderRadius: 10, background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Lock size={16} color="#D97706" />
    </div>
    <div style={{ flex: 1, minWidth: 220 }}>
      <div style={{ fontWeight: 700, fontSize: 13.5, color: '#92400E' }}>You've used your {limit}/{limit} search this month</div>
      <div style={{ fontSize: 12.5, color: '#78350F', marginTop: 2 }}>Upgrade to Pro for unlimited job searches, every month.</div>
    </div>
    <button type="button" onClick={onUpgrade}
      style={{ background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif', whiteSpace: 'nowrap', flexShrink: 0 }}>
      Upgrade to Pro
    </button>
  </div>
);

/* ── Tailor Resume Modal ────────────────────────────────────────────────── */
const TAILOR_STEPS = [
  'Analyzing job description…',
  'Reading your resume…',
  'Matching keywords & skills…',
  'Rewriting summary…',
  'Updating experience bullets…',
  'Finalizing tailored resume…',
];

const TailorResumeModal = ({ job, onClose }) => {
  const navigate = useNavigate();
  const [step,        setStep]       = useState('pick');
  const [docs,        setDocs]       = useState([]);
  const [loadingDocs, setLoadingDocs]= useState(true);
  const [selected,    setSelected]   = useState(null);
  const [tailored,    setTailored]   = useState(null);
  const [tailoredMeta, setTailoredMeta] = useState(null); // { template, type }
  const [error,       setError]      = useState(null);
  const [stepIdx,     setStepIdx]    = useState(0);
  const stepTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('rb_token');
        const res = await fetch(`${BASE}/documents`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        const resumes = (Array.isArray(data) ? data : []).filter(d => d.type === 'resume' || d.type === 'cv');
        setDocs(resumes);
        if (resumes.length) setSelected(resumes[0]);
      } catch (_) {}
      finally { setLoadingDocs(false); }
    })();
  }, []);

  const handleTailor = async () => {
    if (!selected) return;
    setStep('tailoring');
    setStepIdx(0);
    stepTimer.current = setInterval(() => setStepIdx(i => Math.min(i + 1, TAILOR_STEPS.length - 1)), 2200);
    try {
      const token = localStorage.getItem('rb_token');
      const jobCtx = `${job.title} at ${job.company_name}\n\nLocation: ${job.location || ''}\n\nJob Description:\n${job.description || ''}`;
      const aiRes = await fetch(`${BASE}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          messages: [{ role: 'user', content: `Tailor this resume for the job. Adjust summary, reorder/enhance skills, emphasize relevant experience. Keep the exact same JSON structure.\n\nResume JSON:\n${JSON.stringify(selected.data)}\n\nJob:\n${jobCtx}\n\nReturn ONLY the updated resume JSON.` }],
          system_prompt: 'You are an expert career coach. Return ONLY valid JSON matching the input structure exactly, no markdown fences, no extra text.',
          temperature: 0.7,
          max_tokens: 3000,
        }),
      });
      const aiData = await aiRes.json();
      if (!aiRes.ok) throw new Error(aiData.error || 'AI error');
      const tailoredData = _parseJSON(aiData.result || '');
      clearInterval(stepTimer.current);
      setTailored(tailoredData);
      setTailoredMeta({ template: selected.data?.template || 'modern', type: selected.type || 'resume' });
      setStep('done');
    } catch (e) {
      clearInterval(stepTimer.current);
      setError(e.message);
      setStep('pick');
    }
  };

  useEffect(() => () => clearInterval(stepTimer.current), []);

  const doneChecks = tailored ? [
    { label: 'Professional Summary', ok: !!(tailored.personalInfo?.summary) },
    { label: 'Work Experience',      ok: !!(tailored.experience?.length) },
    { label: 'Skills',               ok: !!(tailored.skills?.length) },
    { label: 'Education',            ok: !!(tailored.education?.length) },
    { label: 'Projects',             ok: !!(tailored.projects?.length) },
    { label: 'Certifications',       ok: !!(tailored.certifications?.length) },
  ].filter(c => c.ok) : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter,sans-serif' }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 580, boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden' }}>

        {/* ── PICK STEP ── */}
        {step === 'pick' && (
          <>
            <div style={{ padding: '26px 28px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>Pick a resume to tailor</h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2, marginTop: -2, display: 'flex' }}><X size={20} /></button>
              </div>
              <p style={{ margin: '0 0 20px', color: '#6B7280', fontSize: 13, lineHeight: 1.5 }}>
                AI will tailor your resume for <strong style={{ color: '#111827' }}>{job.title}</strong> at <strong style={{ color: '#EF1D28' }}>{job.company_name}</strong> using the job description.
              </p>
              {error && (
                <div style={{ background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', color: '#EF1D28', fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle size={14} />{error}
                </div>
              )}
              {loadingDocs ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                  <RocketLoader size={120} message="Loading your resumes…" />
                </div>
              ) : docs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF' }}>
                  <FileText size={36} style={{ marginBottom: 10, opacity: 0.35 }} />
                  <div style={{ fontSize: 14, marginBottom: 4 }}>No resumes found</div>
                  <div style={{ fontSize: 12 }}>Create a resume first, then come back to tailor it.</div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8, marginBottom: 4 }}>
                  {docs.map(doc => {
                    const isSel = selected?._id === doc._id;
                    const name  = doc.data?.personalInfo?.fullName || doc.title || 'Untitled';
                    const title = doc.data?.personalInfo?.jobTitle || '—';
                    const date  = doc.updatedAt ? new Date(doc.updatedAt).toLocaleDateString('en-GB') : '';
                    return (
                      <button key={doc._id} onClick={() => setSelected(doc)} style={{
                        flexShrink: 0, width: 160, padding: 0, textAlign: 'left',
                        border: `2.5px solid ${isSel ? '#EF1D28' : '#E5E7EB'}`,
                        borderRadius: 12, background: 'white',
                        cursor: 'pointer', transition: 'all 0.15s',
                        boxShadow: isSel ? '0 0 0 3px rgba(239,29,40,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                      }}>
                        {/* Template thumbnail */}
                        <div style={{ position: 'relative', width: '100%', height: 200, overflow: 'hidden', background: '#F8FAFC', borderBottom: `1px solid ${isSel ? '#FECDD3' : '#F1F5F9'}` }}>
                          <div style={{ width: 700, position: 'absolute', top: 0, left: '50%', marginLeft: -350, transform: 'scale(0.226)', transformOrigin: 'top center', pointerEvents: 'none', userSelect: 'none' }}>
                            <ResumePreview data={doc.data} id={`tailor-preview-${doc._id}`} />
                          </div>
                          {isSel && (
                            <div style={{ position: 'absolute', top: 7, right: 7, width: 22, height: 22, borderRadius: '50%', background: '#EF1D28', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(239,29,40,0.4)' }}>
                              <Check size={12} color="white" strokeWidth={3} />
                            </div>
                          )}
                        </div>
                        {/* Info strip */}
                        <div style={{ padding: '10px 12px' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                          <div style={{ fontSize: 10, color: '#94A3B8' }}>{date}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: '18px 28px 24px', display: 'flex', gap: 10, borderTop: '1px solid #F3F4F6', marginTop: 20 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '11px 0', border: '1px solid #E5E7EB', borderRadius: 10, background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151', fontFamily: 'Inter,sans-serif', fontWeight: 500 }}>Cancel</button>
              <button onClick={handleTailor} disabled={!selected || docs.length === 0} style={{ flex: 2, padding: '11px 0', border: 'none', borderRadius: 10, background: !selected || docs.length === 0 ? '#F3F4F6' : '#EF1D28', color: !selected || docs.length === 0 ? '#9CA3AF' : 'white', fontSize: 13, fontWeight: 700, cursor: !selected || docs.length === 0 ? 'not-allowed' : 'pointer', fontFamily: 'Inter,sans-serif' }}>
                Tailor Now
              </button>
            </div>
          </>
        )}

        {/* ── TAILORING STEP ── */}
        {step === 'tailoring' && (
          <div style={{ padding: '52px 32px 48px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <RocketLoader size={180} message={TAILOR_STEPS[stepIdx]} />
          </div>
        )}

        {/* ── DONE STEP ── */}
        {step === 'done' && (
          <>
            <div style={{ padding: '28px 32px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700, color: '#15803D', marginBottom: 14 }}>
                <CheckCircle2 size={12} />Tailoring complete
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: '#111827' }}>Resume tailoring done!</h2>
              <p style={{ margin: '0 0 22px', color: '#6B7280', fontSize: 13, lineHeight: 1.55 }}>
                AI has tailored your resume for <strong style={{ color: '#111827' }}>{job.title}</strong>. It has been saved as a new document — double-check for accuracy before applying.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
                {doneChecks.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#EF1D28', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check size={12} color="white" strokeWidth={3} />
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: '16px 32px 26px', borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={onClose} style={{ padding: '10px 20px', border: '1px solid #E5E7EB', borderRadius: 10, background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151', fontFamily: 'Inter,sans-serif' }}>Close</button>
              <button onClick={() => {
                onClose();
                navigate(`/builder/${tailoredMeta?.type || 'resume'}/new`, {
                  state: {
                    tailoredData: { ...tailored, template: tailoredMeta?.template || 'modern' },
                    fromJob: { title: job.title, company: job.company_name },
                  },
                });
              }}
                style={{ padding: '10px 28px', border: 'none', borderRadius: 10, background: '#EF1D28', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>
                Continue Editing
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Practice Interview Modal ───────────────────────────────────────────── */
const IV_SENIORITIES = ['junior', 'mid', 'senior'];

const PracticeInterviewModal = ({ job, onClose, onCreated }) => {
  const { generateInterviewQuestions, loading, dailyLimitError, clearDailyLimitError } = useClaudeAI();
  const [mode,      setMode]      = useState('basic');
  const [step,      setStep]      = useState(1);
  const [jobTitle,  setJobTitle]  = useState(job?.title || '');
  const [jobAd,     setJobAd]     = useState(job?.description?.slice(0, 4000) || '');
  const [seniority, setSeniority] = useState('mid');
  const [error,     setError]     = useState(null);

  const canNext     = jobTitle.trim() && (mode === 'basic' || jobAd.trim());
  const canGenerate = jobTitle.trim() && !loading;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setError(null);
    try {
      const result = await generateInterviewQuestions({
        jobTitle: jobTitle.trim(),
        seniority,
        mode,
        jobAd: mode === 'personalized' ? jobAd : '',
        resumeData: null,
      });
      const interview = await api.createInterview({
        title: jobTitle.trim(),
        mode,
        seniority,
        jobAd: mode === 'personalized' ? jobAd : '',
        questions: result.questions || [],
      });
      onCreated(interview);
    } catch (err) {
      setError(err.message || 'Failed to generate questions');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter,sans-serif' }}>
      <div style={{ background: 'white', borderRadius: 18, padding: '28px 28px 24px', width: '100%', maxWidth: 430, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.22)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF', padding: 4, display: 'flex' }}>
          <X size={20} />
        </button>

        <h2 style={{ margin: '0 0 18px', fontSize: 20, fontWeight: 800, color: '#111827' }}>Job Interview Questions</h2>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {[['basic', '🕐 Basic'], ['personalized', '⊕ Personalized']].map(([id, label]) => (
            <button key={id} onClick={() => { setMode(id); setStep(1); }} style={{
              flex: 1, border: `1.5px solid ${mode === id ? '#EF1D28' : '#E5E7EB'}`,
              borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: 'white', color: mode === id ? '#EF1D28' : '#6B7280',
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* Description */}
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6B7280', lineHeight: 1.55 }}>
          {mode === 'basic'
            ? 'In Basic mode, quickly generates a set of interview questions tailored to your desired job position.'
            : 'In Personalized mode, generates interview questions tailored to the job you want, using the full job description as context.'}
        </p>

        {mode === 'personalized' && (
          <div style={{ textAlign: 'center', color: '#6B7280', fontSize: 12, fontWeight: 600, marginBottom: 16 }}>Step {step}/2</div>
        )}

        {step === 1 && (
          <>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Job title from the job ad <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              value={jobTitle}
              onChange={e => setJobTitle(e.target.value)}
              placeholder="e.g. Software Engineer"
              style={{ width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 16, fontFamily: 'Inter,sans-serif' }}
              onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
            />

            {mode === 'personalized' && (
              <>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Job description <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <textarea
                  value={jobAd}
                  onChange={e => setJobAd(e.target.value.slice(0, 5000))}
                  rows={6}
                  placeholder="Paste the full job description here…"
                  style={{ width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Inter,sans-serif', marginBottom: 4 }}
                  onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
                  onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
                />
                <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF', marginBottom: 14 }}>{jobAd.length}/5000</div>
              </>
            )}

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Seniority</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              {IV_SENIORITIES.map(s => (
                <button key={s} onClick={() => setSeniority(s)} style={{
                  flex: 1, border: seniority === s ? '2px solid #111827' : '1.5px solid #E5E7EB',
                  borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: seniority === s ? 700 : 500,
                  cursor: 'pointer', background: 'white', color: seniority === s ? '#111827' : '#6B7280', textTransform: 'capitalize',
                }}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 2 && mode === 'personalized' && (
          <div style={{ marginBottom: 24, padding: '14px 16px', background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Ready to generate!</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>Questions will be personalized based on the job description and your seniority level.</div>
          </div>
        )}

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#EF1D28', background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
            <AlertCircle size={14} />{error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {mode === 'personalized' && step === 1 ? (
            <button onClick={() => setStep(2)} disabled={!canNext} style={{
              background: canNext ? '#EF1D28' : '#F3F4F6', color: canNext ? 'white' : '#9CA3AF',
              border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700,
              cursor: canNext ? 'pointer' : 'not-allowed',
            }}>
              Next →
            </button>
          ) : (
            <button onClick={handleGenerate} disabled={!canGenerate} style={{
              background: !canGenerate ? '#F3F4F6' : 'linear-gradient(135deg,#EF1D28,#C5161D)',
              color: !canGenerate ? '#9CA3AF' : 'white', border: 'none', borderRadius: 10,
              padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: !canGenerate ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {loading
                ? <><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} />Generating…</>
                : <><Sparkles size={16} />Generate with AI</>}
            </button>
          )}
        </div>
      </div>
      {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}
    </div>
  );
};

/* ── Cover Letter Modal ─────────────────────────────────────────────────── */
const CoverLetterModal = ({ job, onClose }) => {
  const navigate             = useNavigate();
  const { generateResume, loading, dailyLimitError: clDailyLimit, clearDailyLimitError: clearClDailyLimit } = useClaudeAI();
  const [mode,          setMode]          = useState('basic');
  const [jobTitle,      setJobTitle]      = useState(job?.title || '');
  const [company,       setCompany]       = useState(job?.company_name || '');
  const [hiringManager, setHiringManager] = useState('');
  const [jobDesc,       setJobDesc]       = useState(job?.description?.slice(0, 5000) || '');
  const [userBio,       setUserBio]       = useState('');
  const [error,         setError]         = useState(null);

  const CL_ACCENT = '#16A34A';
  const canGenerate = jobTitle.trim() && !loading;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setError(null);
    try {
      const input = {
        jobTitle:       jobTitle.trim(),
        company:        company.trim(),
        hiringManager:  hiringManager.trim(),
        jobDescription: jobDesc.trim(),
        ...(mode === 'personalized' && userBio.trim() ? { userBackground: userBio.trim() } : {}),
      };
      const generated = await generateResume(input, 'cover_letter');
      const clData = {
        ...generated,
        type:     'cover_letter',
        template: 'cl_classic',
        jobInfo: {
          company:        company.trim()        || generated.jobInfo?.company       || '',
          role:           jobTitle.trim()        || generated.jobInfo?.role          || '',
          hiringManager:  hiringManager.trim()   || generated.jobInfo?.hiringManager || '',
        },
      };
      onClose();
      navigate('/builder/cover_letter/new', {
        state: { tailoredData: clData, fromJob: { title: job.title, company: job.company_name } },
      });
    } catch (err) {
      setError(err.message || 'Failed to generate cover letter');
    }
  };

  const inputStyle = (accent) => ({
    width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 8,
    padding: '10px 12px', fontSize: 13.5, outline: 'none',
    boxSizing: 'border-box', fontFamily: 'Inter,sans-serif',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.52)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Inter,sans-serif' }}>
      <div style={{ background: 'white', borderRadius: 18, padding: '28px 28px 26px', width: '100%', maxWidth: 460, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.22)', maxHeight: '92vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF', padding: 4, display: 'flex' }}>
          <X size={20} />
        </button>

        <h2 style={{ margin: '0 0 18px', fontSize: 20, fontWeight: 800, color: '#111827' }}>AI Cover Letter Writer</h2>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: '#F3F4F6', borderRadius: 10, padding: 4 }}>
          {[['basic', '🕐', 'Basic'], ['personalized', '⊕', 'Personalized']].map(([id, ico, label]) => (
            <button key={id} onClick={() => setMode(id)} style={{
              flex: 1, border: mode === id ? `1.5px solid ${CL_ACCENT}` : '1.5px solid transparent',
              borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: mode === id ? 'white' : 'transparent',
              color: mode === id ? CL_ACCENT : '#6B7280',
              boxShadow: mode === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <span>{ico}</span>{label}
            </button>
          ))}
        </div>

        {/* Description */}
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
          {mode === 'basic'
            ? 'In Basic mode, the AI Writer will quickly produce a solid, generic draft of a cover letter tailored to your desired job position.'
            : 'In Personalized mode, the AI tailors the cover letter to your background and experience, making it stand out from the crowd.'}
        </p>

        {/* Job Title */}
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: CL_ACCENT, marginBottom: 5 }}>
          Job title on the application <span style={{ color: '#EF4444' }}>(required)</span>
        </label>
        <input value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Senior Project Manager"
          style={{ ...inputStyle(), marginBottom: 14 }}
          onFocus={e => { e.target.style.borderColor = CL_ACCENT; }}
          onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }} />

        {/* Company */}
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Company name</label>
        <input value={company} onChange={e => setCompany(e.target.value)} placeholder="e.g. Google"
          style={{ ...inputStyle(), marginBottom: 14 }}
          onFocus={e => { e.target.style.borderColor = CL_ACCENT; }}
          onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }} />

        {/* Hiring manager (personalized only) */}
        {mode === 'personalized' && (
          <>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Hiring manager's name (optional)</label>
            <input value={hiringManager} onChange={e => setHiringManager(e.target.value)} placeholder="e.g. Sarah Johnson"
              style={{ ...inputStyle(), marginBottom: 14 }}
              onFocus={e => { e.target.style.borderColor = CL_ACCENT; }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }} />

            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Your background & experience</label>
            <textarea value={userBio} onChange={e => setUserBio(e.target.value.slice(0, 2000))} rows={3}
              placeholder="Briefly describe your experience, key skills, and why you're a great fit…"
              style={{ ...inputStyle(), resize: 'vertical', marginBottom: 4 }}
              onFocus={e => { e.target.style.borderColor = CL_ACCENT; }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }} />
            <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF', marginBottom: 14 }}>{userBio.length}/2000</div>
          </>
        )}

        {/* Job description */}
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Copy and paste the job post here</label>
        <textarea value={jobDesc} onChange={e => setJobDesc(e.target.value.slice(0, 6000))} rows={7}
          style={{ ...inputStyle(), resize: 'vertical', marginBottom: 4 }}
          onFocus={e => { e.target.style.borderColor = CL_ACCENT; }}
          onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }} />
        <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF', marginBottom: 20 }}>{jobDesc.length}/6000</div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#EF1D28', background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
            <AlertCircle size={14} />{error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handleGenerate} disabled={!canGenerate} style={{
            background: !canGenerate ? '#F3F4F6' : `linear-gradient(135deg,${CL_ACCENT},#14532D)`,
            color: !canGenerate ? '#9CA3AF' : 'white', border: 'none', borderRadius: 10,
            padding: '12px 28px', fontSize: 14, fontWeight: 700,
            cursor: !canGenerate ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Inter,sans-serif',
          }}>
            {loading
              ? <><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} />Generating…</>
              : <><Sparkles size={16} />Generate with AI</>}
          </button>
        </div>
      </div>
      {clDailyLimit && <DailyLimitModal onClose={clearClDailyLimit} />}
    </div>
  );
};

/* ── Skeleton loading card ──────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div style={{ padding: '14px 16px', borderRadius: 10, border: '1px solid #E5E7EB', background: 'white', marginBottom: 8 }}>
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ width: 40, height: 40, borderRadius: 8, background: '#F1F5F9', animation: 'shimmer 1.4s ease-in-out infinite', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 13, width: '58%', background: '#F1F5F9', borderRadius: 4, marginBottom: 7, animation: 'shimmer 1.4s ease-in-out infinite' }} />
        <div style={{ height: 11, width: '32%', background: '#F1F5F9', borderRadius: 4, marginBottom: 10, animation: 'shimmer 1.4s ease-in-out 0.1s infinite' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ height: 18, width: 64, background: '#F1F5F9', borderRadius: 4, animation: 'shimmer 1.4s ease-in-out 0.2s infinite' }} />
          <div style={{ height: 18, width: 48, background: '#F1F5F9', borderRadius: 4, animation: 'shimmer 1.4s ease-in-out 0.3s infinite' }} />
        </div>
      </div>
    </div>
  </div>
);

/* ── Free-plan monthly job search quota (Pro is unlimited) ─────────────── */
const FREE_SEARCH_LIMIT = 1;

/* ── Main Page ──────────────────────────────────────────────────────────── */
export const JobSearchPage = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const isPro = user?.plan === 'pro';
  const searchesUsed = (!isPro && user?.jobSearchUsage?.monthKey === new Date().toISOString().slice(0, 7))
    ? user.jobSearchUsage.count : 0;
  const searchLimitReached = !isPro && searchesUsed >= FREE_SEARCH_LIMIT;

  const [inputVal,     setInputVal]     = useState('');
  const [query,        setQuery]        = useState('');
  const [country,      setCountry]      = useState('');
  const [city,         setCity]         = useState('');
  const [filters,      setFilters]      = useState({});
  const [showFilters,  setShowFilters]  = useState(false);
  const [jobs,         setJobs]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [needsKey,     setNeedsKey]     = useState(false);
  /* Job providers unreachable / out of quota — show the empty state with a
     support hint instead of an API error the user can do nothing about. */
  const [providersDown, setProvidersDown] = useState(false);

  /* Job Tracker bridge: bookmarking and applying create/advance a board card. */
  const tracker = useTrackedJobs();
  const [toast, setToast] = useState(null);   // { message, undo?, actionLabel? }
  const toastTimer = useRef(null);

  const showToast = useCallback((next) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = setTimeout(() => setToast(null), 7000);
  }, []);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const handleTrack = useCallback(async (job) => {
    const card = await tracker.bookmark(job);
    if (card) showToast({ message: `Added “${job.title}” to Job Tracker` });
  }, [tracker, showToast]);

  const handleUntrack = useCallback(async (job) => {
    await tracker.untrack(job);
    showToast({ message: `Removed “${job.title}” from Job Tracker` });
  }, [tracker, showToast]);

  /* Apply Now sends the user to the employer's site, where we cannot see what
     they did. Rather than guess, remember the job and ask once they come back. */
  const pendingApply = useRef(null);
  const [applyAsk, setApplyAsk] = useState(null);

  const handleApply = useCallback((job) => {
    if (tracker.locked) return;              // Pro-only feature
    const card = tracker.cardFor(job);
    if (card?.stage === 'applied') return;   // already recorded
    pendingApply.current = { job, at: Date.now() };
  }, [tracker]);

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      const pending = pendingApply.current;
      // Ignore an instant tab flick — they never got as far as the listing.
      if (!pending || Date.now() - pending.at < 2500) return;
      pendingApply.current = null;
      setApplyAsk(pending.job);
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);

  const confirmApplied = useCallback(async (job) => {
    setApplyAsk(null);
    const result = await tracker.markApplied(job);
    if (result) showToast({ message: `“${job.title}” moved to Applied`, undo: result.undo });
  }, [tracker, showToast]);
  const [page,         setPage]         = useState(1);
  const [hasMore,      setHasMore]      = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [selectedJob,  setSelectedJob]  = useState(null);
  const [showTailor,      setShowTailor]      = useState(false);
  const [showPractice,    setShowPractice]    = useState(false);
  const [showCoverLetter, setShowCoverLetter] = useState(false);
  const [viewTab,      setViewTab]      = useState('all'); // 'all' | 'saved'
  const [savedJobs,    setSavedJobs]    = useState(() => {
    try { return JSON.parse(localStorage.getItem('mrs_saved_jobs') || '[]'); } catch { return []; }
  });
  const [hasSearched, setHasSearched] = useState(false);
  const abortRef = useRef(null);

  // Persist saved jobs to localStorage whenever they change
  useEffect(() => {
    try { localStorage.setItem('mrs_saved_jobs', JSON.stringify(savedJobs)); } catch {}
  }, [savedJobs]);

  const isJobSaved = (job) => savedJobs.some(j => j.slug === job.slug);

  const handleToggleSave = (job) => {
    setSavedJobs(prev => {
      const already = prev.some(j => j.slug === job.slug);
      if (already) return prev.filter(j => j.slug !== job.slug);
      return [{ ...job, savedAt: Date.now() }, ...prev];
    });
  };

  const load = useCallback(async (q, pg, c, ci, f, replace = false) => {
    /* Free-plan monthly quota already used — block further fresh searches client-side too */
    if (pg === 1 && searchLimitReached) {
      setError(`You've used your ${FREE_SEARCH_LIMIT}/${FREE_SEARCH_LIMIT} search this month. Upgrade to Pro for unlimited job search.`);
      return;
    }

    /* Cancel any in-flight request so stale results never overwrite newer ones */
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true); setError(null); setNeedsKey(false); setProvidersDown(false);
    if (replace) setJobs([]);   // clear immediately so skeletons show while fetching
    try {
      const isRemote = c === 'Remote' || c === 'Anywhere';
      const jobType  = (f.jobTypes || [])[0] || '';
      const data = await searchJobs({ query: q, page: pg, country: c, city: ci, jobType, remote: isRemote, datePosted: f.datePosted || 'all', signal: controller.signal });
      if (controller.signal.aborted) return;
      if (data.providersUnavailable) setProvidersDown(true);
      const items = data.jobs || [];
      setJobs(prev => replace ? items : [...prev, ...items]);
      setHasMore(items.length >= 10);
      setPage(pg);
      if (replace) setVisibleCount(10);
      if (replace && items.length > 0) setSelectedJob(items[0]);
      /* Sync the updated quota count from the server after a fresh free-plan search */
      if (pg === 1 && !isPro) refreshUser().catch(() => {});
    } catch (e) {
      if (e.name === 'AbortError') return;
      if (e.needsKey) setNeedsKey(true);
      else if (e.providersUnavailable) setProvidersDown(true);
      else setError(e.message);
      if (e.error === 'monthly_search_limit_exceeded') refreshUser().catch(() => {});
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [searchLimitReached, isPro, refreshUser]);

  const handleSearch = () => {
    setHasSearched(true); setQuery(inputVal); setSelectedJob(null);
    load(inputVal, 1, country, city, filters, true);
  };

  const handleStartExploring = () => {
    setHasSearched(true); setSelectedJob(null);
    load(inputVal, 1, country, city, filters, true);
  };

  const handleTrending = (role) => {
    setHasSearched(true); setInputVal(role); setQuery(role); setSelectedJob(null);
    load(role, 1, country, city, filters, true);
  };

  const handleCountryChange = (c) => {
    setCountry(c); setSelectedJob(null);
    if (hasSearched) load(query, 1, c, city, filters, true);
  };

  const handleApplyFilters = () => {
    setShowFilters(false); setSelectedJob(null);
    if (hasSearched) load(query, 1, country, city, filters, true);
  };

  /* Reveal already-fetched jobs 10 at a time; only hit the API once the buffer runs out */
  const handleLoadMore = () => {
    if (!isPro) return; // free plan is capped to the first 10 results per search
    setVisibleCount(v => v + 10);
    if (visibleCount + 10 >= jobs.length && hasMore) load(query, page + 1, country, city, filters, false);
  };

  const shownVisibleCount = isPro ? visibleCount : Math.min(visibleCount, 10);
  const canLoadMore = isPro && (visibleCount < jobs.length || hasMore);
  const freeResultsCapped = !isPro && (jobs.length > 10 || hasMore);

  const activeFilterCount = [
    (country || city) ? 1 : 0,
    (filters.jobTypes || []).length,
    filters.datePosted && filters.datePosted !== 'all' ? 1 : 0,
    filters.salary ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div style={{ fontFamily: 'Inter,sans-serif', display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
        @keyframes ddFadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        .job-search-input:focus { outline: none; }
        .job-filter-panel { position: fixed; top: 180px; right: 16px; }
        .pill-dd-panel { animation: ddFadeIn 0.15s ease; }
        @media (max-width: 640px) {
          .job-filter-panel { right: 8px !important; left: 8px !important; width: auto !important; }
          .job-split-layout { flex-direction: column !important; }
          .job-right-panel { display: none; }
        }
      `}</style>

      {needsKey && <ApiKeyNotice />}
      {searchLimitReached && <SearchLimitNotice limit={FREE_SEARCH_LIMIT} onUpgrade={() => navigate('/pricing')} />}

      {/* ── Search bar ── */}
      <div style={{ background: 'white', border: '1.5px solid #D1D5DB', borderRadius: 12, display: 'flex', alignItems: 'center', overflow: 'hidden', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Search size={17} color="#94A3B8" style={{ marginLeft: 16, flexShrink: 0 }} />
        <input
          className="job-search-input"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !searchLimitReached && handleSearch()}
          placeholder="Job title, keyword, or company…"
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, color: '#111827', padding: '14px 12px', background: 'transparent', fontFamily: 'Inter,sans-serif' }}
        />
        {inputVal && (
          <button onClick={() => { setInputVal(''); setQuery(''); if (hasSearched) load('', 1, country, city, filters, true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 6, display: 'flex', flexShrink: 0 }}>
            <X size={14} />
          </button>
        )}
        <button onClick={handleSearch} disabled={searchLimitReached} title={searchLimitReached ? `You've used your ${FREE_SEARCH_LIMIT}/${FREE_SEARCH_LIMIT} search this month` : undefined}
          style={{ background: searchLimitReached ? '#E5E7EB' : 'linear-gradient(135deg,#EF1D28,#C5161D)', color: searchLimitReached ? '#9CA3AF' : 'white', border: 'none', borderRadius: 8, margin: 6, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: searchLimitReached ? 'not-allowed' : 'pointer', fontFamily: 'Inter,sans-serif', display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, whiteSpace: 'nowrap', boxShadow: searchLimitReached ? 'none' : '0 2px 8px rgba(239,29,40,0.3)' }}>
          {searchLimitReached ? <Lock size={14} /> : <Search size={14} strokeWidth={2.5} />}{searchLimitReached ? 'Limit reached' : 'Search'}
        </button>
      </div>

      {/* ── Filter chips ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none', alignItems: 'center' }}>

        {/* Location */}
        <PillDropdown label={city && country ? `${city}, ${country}` : city || country || 'Location'} active={!!(country || city)} width={300}>
          {({ close }) => (
            <LocationContent current={{ country, city }} onApply={({ country: c, city: ci }) => { setCountry(c); setCity(ci); setSelectedJob(null); if (hasSearched) load(query, 1, c, ci, filters, true); close(); }} />
          )}
        </PillDropdown>

        {/* Type */}
        <PillDropdown
          label={(filters.jobTypes||[]).length ? (filters.jobTypes||[]).join(', ') : 'Type'}
          active={(filters.jobTypes||[]).length > 0}
          width={260}
        >
          {({ close }) => (
            <TypeContent current={filters.jobTypes} onApply={(types) => {
              const f = { ...filters, jobTypes: types };
              setFilters(f); setSelectedJob(null); if (hasSearched) load(query, 1, country, city, f, true); close();
            }} />
          )}
        </PillDropdown>

        {/* Salary */}
        <PillDropdown
          label={filters.salary ? `${SALARY_OPTIONS.find(o=>o.val===filters.salary)?.label || filters.salary}` : 'Salary'}
          active={!!filters.salary}
          width={220}
        >
          {({ close }) => (
            <SalaryContent current={filters.salary} onApply={(s) => { setFilters(f => ({ ...f, salary: s })); close(); }} />
          )}
        </PillDropdown>

        {/* Date posted */}
        <PillDropdown
          label={filters.datePosted && filters.datePosted !== 'all' ? FILTER_DATE.find(d=>d.val===filters.datePosted)?.label : 'Date posted'}
          active={!!(filters.datePosted && filters.datePosted !== 'all')}
          width={220}
        >
          {({ close }) => (
            <DateContent current={filters.datePosted} onApply={(d) => {
              const f = { ...filters, datePosted: d };
              setFilters(f); setSelectedJob(null); if (hasSearched) load(query, 1, country, city, f, true); close();
            }} />
          )}
        </PillDropdown>

        {/* Companies */}
        <PillDropdown label="Companies" active={false} width={290}>
          {({ close }) => (
            <CompaniesContent onSearch={(q) => { setHasSearched(true); setInputVal(q); setQuery(q); setSelectedJob(null); load(q, 1, country, city, filters, true); close(); }} />
          )}
        </PillDropdown>

        {/* Keywords */}
        <PillDropdown label="Keywords" active={false} width={290}>
          {({ close }) => (
            <KeywordsContent onSearch={(kw) => { const nq = query ? `${query} ${kw}` : kw; setHasSearched(true); setInputVal(nq); setQuery(nq); setSelectedJob(null); load(nq, 1, country, city, filters, true); close(); }} />
          )}
        </PillDropdown>

        {/* All Filters icon button */}
        <button onClick={() => setShowFilters(p => !p)} title="All Filters" style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', flexShrink: 0,
          width: 34, height: 34,
          border: `1.5px solid ${activeFilterCount > 0 ? '#EF1D28' : showFilters ? '#94A3B8' : '#D1D5DB'}`,
          borderRadius: '50%',
          background: activeFilterCount > 0 ? '#FFF1F2' : showFilters ? '#F8FAFC' : 'white',
          color: activeFilterCount > 0 ? '#EF1D28' : '#374151',
          cursor: 'pointer', transition: 'all 0.15s',
        }}>
          <SlidersHorizontal size={14} />
          {activeFilterCount > 0 && (
            <span style={{ position: 'absolute', top: -5, right: -5, width: 16, height: 16, background: '#EF1D28', color: 'white', borderRadius: '50%', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter,sans-serif', border: '2px solid white' }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {showFilters && (
        <FilterPanel filters={filters} setFilters={setFilters} country={country} setCountry={c => setCountry(c)} city={city} setCity={setCity} onClose={() => setShowFilters(false)} onApply={handleApplyFilters} />
      )}

      {/* ── Empty state (before first search) ── */}
      {!hasSearched && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 480, textAlign: 'center', padding: '48px 24px', background: 'white', border: '1px solid #E2E8F0', borderRadius: 14 }}>
          <div style={{ width: 88, height: 88, borderRadius: 28, background: 'linear-gradient(135deg,#FFF1F2,#FFE4E6)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 22, boxShadow: '0 4px 20px rgba(239,29,40,0.12)' }}>
            <Briefcase size={40} color="#EF1D28" strokeWidth={1.5} />
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 10, letterSpacing: -0.3 }}>Find Your Next Opportunity</div>
          <div style={{ fontSize: 14, color: '#6B7280', maxWidth: 360, lineHeight: 1.75, marginBottom: 30 }}>
            Type a job title in the search bar above, or click below to browse thousands of live listings.
          </div>
          <button onClick={handleStartExploring} disabled={searchLimitReached}
            style={{ background: searchLimitReached ? '#E5E7EB' : 'linear-gradient(135deg,#EF1D28,#C5161D)', color: searchLimitReached ? '#9CA3AF' : 'white', border: 'none', borderRadius: 12, padding: '13px 34px', fontSize: 15, fontWeight: 700, cursor: searchLimitReached ? 'not-allowed' : 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: searchLimitReached ? 'none' : '0 4px 18px rgba(239,29,40,0.35)', letterSpacing: 0.1 }}>
            {searchLimitReached ? <Lock size={16} /> : <Search size={16} strokeWidth={2.5} />}{searchLimitReached ? 'Upgrade to search' : 'Start Exploring Jobs'}
          </button>
          <div style={{ marginTop: 32, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 500 }}>
            {['Software Engineer', 'Nurse Practitioner', 'Product Manager', 'Data Analyst', 'UX Designer', 'Marketing Manager'].map(role => (
              <button key={role} onClick={() => !searchLimitReached && handleTrending(role)} disabled={searchLimitReached}
                style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 500, color: searchLimitReached ? '#C0C6D0' : '#374151', cursor: searchLimitReached ? 'not-allowed' : 'pointer', fontFamily: 'Inter,sans-serif', transition: 'all 0.15s' }}
                onMouseEnter={e => { if (searchLimitReached) return; e.currentTarget.style.borderColor = '#EF1D28'; e.currentTarget.style.color = '#EF1D28'; e.currentTarget.style.background = '#FFF1F2'; }}
                onMouseLeave={e => { if (searchLimitReached) return; e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = '#F8FAFC'; }}>
                {role}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Split layout ── */}
      {hasSearched && <div className="job-split-layout" style={{ display: 'flex', gap: 0, flex: 1, border: '1px solid #E2E8F0', borderRadius: 14, overflow: 'hidden', background: 'white', minHeight: 560 }}>

        {/* Left: job list */}
        <div style={{ width: 460, borderRight: '1px solid #E2E8F0', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Tab row */}
          <div style={{ display: 'flex', borderBottom: '1px solid #F0F2F5', flexShrink: 0 }}>
            <button onClick={() => setViewTab('all')} style={{
              flex: 1, padding: '10px 0', border: 'none', background: 'none', cursor: 'pointer',
              fontFamily: 'Inter,sans-serif', fontSize: 13, fontWeight: viewTab === 'all' ? 700 : 500,
              color: viewTab === 'all' ? '#EF1D28' : '#6B7280',
              borderBottom: `2px solid ${viewTab === 'all' ? '#EF1D28' : 'transparent'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'color 0.15s',
            }}>
              <Search size={13} />
              {query ? `"${query}"` : 'All Jobs'}
              {!loading && jobs.length > 0 && (
                <span style={{ background: viewTab === 'all' ? '#FFF1F2' : '#F3F4F6', color: viewTab === 'all' ? '#EF1D28' : '#9CA3AF', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                  {jobs.length}
                </span>
              )}
            </button>
            <button onClick={() => setViewTab('saved')} style={{
              flex: 1, padding: '10px 0', border: 'none', background: 'none', cursor: 'pointer',
              fontFamily: 'Inter,sans-serif', fontSize: 13, fontWeight: viewTab === 'saved' ? 700 : 500,
              color: viewTab === 'saved' ? '#EF1D28' : '#6B7280',
              borderBottom: `2px solid ${viewTab === 'saved' ? '#EF1D28' : 'transparent'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'color 0.15s',
            }}>
              <Bookmark size={13} />
              Saved Jobs
              {savedJobs.length > 0 && (
                <span style={{ background: viewTab === 'saved' ? '#FFF1F2' : '#F3F4F6', color: viewTab === 'saved' ? '#EF1D28' : '#9CA3AF', borderRadius: 20, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                  {savedJobs.length}
                </span>
              )}
            </button>
          </div>

          {/* Sub-header: filter info / clear */}
          {viewTab === 'all' && activeFilterCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 14px', borderBottom: '1px solid #F8FAFC', flexShrink: 0 }}>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                {(city || country) && `in ${[city, country].filter(Boolean).join(', ')}`}{(activeFilterCount - ((country || city) ? 1 : 0)) > 0 && ` · ${activeFilterCount - ((country || city) ? 1 : 0)} filter${activeFilterCount - ((country || city) ? 1 : 0) > 1 ? 's' : ''}`}
              </span>
              <button onClick={() => { setFilters({}); setCountry(''); setCity(''); if (hasSearched) load(query, 1, '', '', {}, true); }}
                style={{ background: 'none', border: 'none', color: '#EF1D28', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontFamily: 'Inter,sans-serif' }}>
                <X size={9} />Clear
              </button>
            </div>
          )}

          {/* Job items */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* ── All Jobs tab ── */}
            {viewTab === 'all' && (
              <>
                {error && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#EF1D28', background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 10, padding: '10px 14px', fontSize: 13, margin: '12px 14px' }}>
                    <AlertCircle size={15} />{error}
                  </div>
                )}
                {loading && jobs.length === 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <RocketLoader size={180} message="Searching jobs…" />
                  </div>
                ) : (
                  <>
                    {jobs.length === 0 && !loading && !needsKey && (
                      <div style={{ textAlign: 'center', padding: '48px 20px', color: '#9CA3AF' }}>
                        <Search size={28} color="#D1D5DB" style={{ marginBottom: 10 }} />
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 5 }}>No jobs found</div>
                        {providersDown ? (
                          <div style={{ fontSize: 12, lineHeight: 1.65, maxWidth: 320, margin: '0 auto' }}>
                            We couldn’t load listings for this search. Please try again in a few minutes — if it keeps happening, contact{' '}
                            <a href="mailto:support@marsresume.com?subject=Job%20Search%20unavailable"
                              style={{ color: '#EF1D28', fontWeight: 600, textDecoration: 'none' }}>support@marsresume.com</a>.
                          </div>
                        ) : (
                          <div style={{ fontSize: 12 }}>Try a different keyword or clear your filters.</div>
                        )}
                      </div>
                    )}
                    {jobs.slice(0, shownVisibleCount).map(job => (
                      <JobCard key={job.slug} job={job}
                        selected={selectedJob?.slug === job.slug}
                        onClick={() => setSelectedJob(job)}
                        saved={isJobSaved(job)}
                        onToggleSave={handleToggleSave}
                        trackCard={tracker.cardFor(job)}
                        trackBusy={tracker.isBusy(job)} trackLocked={tracker.locked}
                        onTrack={() => handleTrack(job)}
                        onUntrack={() => handleUntrack(job)} />
                    ))}
                    {loading && jobs.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                        <RocketLoader size={100} message="Loading more…" />
                      </div>
                    )}
                    {(canLoadMore || freeResultsCapped) && !loading && (
                      <div style={{ padding: '12px 16px' }}>
                        <button onClick={handleLoadMore} disabled={!isPro}
                          title={!isPro ? 'Upgrade to Pro to see more than 10 results per search' : undefined}
                          style={{ width: '100%', padding: '10px 0', border: `1.5px solid ${isPro ? '#E5E7EB' : '#FDE68A'}`, borderRadius: 10, background: isPro ? 'white' : '#FFFBEB', color: isPro ? '#374151' : '#92400E', fontSize: 13, fontWeight: 600, cursor: isPro ? 'pointer' : 'not-allowed', fontFamily: 'Inter,sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                          {isPro ? 'Load more jobs →' : <><Lock size={13} /> Upgrade to Pro for more results</>}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* ── Saved Jobs tab ── */}
            {viewTab === 'saved' && (
              <>
                {savedJobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '56px 24px', color: '#9CA3AF' }}>
                    <Bookmark size={32} color="#E5E7EB" style={{ marginBottom: 12 }} />
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 6 }}>No saved jobs yet</div>
                    <div style={{ fontSize: 12, lineHeight: 1.6 }}>Click the bookmark icon on any job card to save it here for later.</div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: '6px 14px 4px', fontSize: 11, color: '#9CA3AF' }}>
                      {savedJobs.length} saved job{savedJobs.length !== 1 ? 's' : ''}
                    </div>
                    {savedJobs.map(job => (
                      <JobCard key={job.slug} job={job}
                        selected={selectedJob?.slug === job.slug}
                        onClick={() => setSelectedJob(job)}
                        saved={true}
                        onToggleSave={handleToggleSave}
                        trackCard={tracker.cardFor(job)}
                        trackBusy={tracker.isBusy(job)} trackLocked={tracker.locked}
                        onTrack={() => handleTrack(job)}
                        onUntrack={() => handleUntrack(job)} />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right: job detail */}
        <div className="job-right-panel" style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
          {selectedJob ? (
            <>
              {showTailor && <TailorResumeModal job={selectedJob} onClose={() => setShowTailor(false)} />}
              {showPractice && (
                <PracticeInterviewModal
                  job={selectedJob}
                  onClose={() => setShowPractice(false)}
                  onCreated={(interview) => { setShowPractice(false); onNavigate?.('interviews', { interview }); }}
                />
              )}
              {showCoverLetter && <CoverLetterModal job={selectedJob} onClose={() => setShowCoverLetter(false)} />}
              <JobDetail
                job={selectedJob}
                onTailorResume={() => setShowTailor(true)}
                onPracticeInterview={() => setShowPractice(true)}
                onWriteCoverLetter={() => setShowCoverLetter(true)}
                saved={isJobSaved(selectedJob)}
                onToggleSave={handleToggleSave}
                trackCard={tracker.cardFor(selectedJob)}
                trackBusy={tracker.isBusy(selectedJob)} trackLocked={tracker.locked}
                onTrack={() => handleTrack(selectedJob)}
                onUntrack={() => handleUntrack(selectedJob)}
                onApply={handleApply}
              />
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 40, color: '#9CA3AF', textAlign: 'center' }}>
              <Briefcase size={40} color="#E5E7EB" style={{ marginBottom: 14 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Select a job to view details</div>
              <div style={{ fontSize: 13, maxWidth: 260, lineHeight: 1.6 }}>Click any job on the left to see the full description and apply.</div>
            </div>
          )}
        </div>
      </div>}

      {/* ── "Did you apply?" — asked when the user comes back from the listing ── */}
      {applyAsk && (
        <div role="dialog" aria-label="Confirm application" style={{
          position: 'fixed', left: '50%', bottom: 26, transform: 'translateX(-50%)',
          zIndex: 410, background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 14,
          padding: '14px 16px', maxWidth: 'min(420px, calc(100vw - 32px))',
          boxShadow: '0 16px 40px rgba(15,23,42,0.20)', fontFamily: 'Inter,sans-serif',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, marginBottom: 12 }}>
            <KanbanSquare size={17} style={{ color: '#0EA5E9', flexShrink: 0, marginTop: 1 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
                Did you apply to this job?
              </div>
              <div style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {applyAsk.title} · {applyAsk.company_name}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setApplyAsk(null)}
              style={{ border: '1.5px solid #E5E7EB', background: 'white', color: '#374151', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Not yet
            </button>
            <button type="button" onClick={() => confirmApplied(applyAsk)}
              style={{ border: 'none', background: 'linear-gradient(135deg,#EF1D28,#C5161D)', color: 'white', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(239,29,40,0.28)' }}>
              Yes, mark as Applied
            </button>
          </div>
        </div>
      )}

      {/* ── Job Tracker toast ── */}
      {toast && !applyAsk && (
        <div role="status" style={{
          position: 'fixed', left: '50%', bottom: 26, transform: 'translateX(-50%)',
          zIndex: 400, display: 'flex', alignItems: 'center', gap: 14,
          background: '#111827', color: 'white', borderRadius: 12,
          padding: '12px 16px', maxWidth: 'min(460px, calc(100vw - 32px))',
          boxShadow: '0 12px 32px rgba(15,23,42,0.28)', fontFamily: 'Inter,sans-serif',
        }}>
          <KanbanSquare size={16} style={{ flexShrink: 0, color: '#5EEAD4' }} />
          <span style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0 }}>{toast.message}</span>
          {toast.undo && (
            <button type="button"
              onClick={async () => { const snap = toast.undo; setToast(null); await tracker.undoApplied(snap); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#FCA5A5', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0, flexShrink: 0 }}>
              <Undo2 size={13} />Undo
            </button>
          )}
          {onNavigate && (
            <button type="button"
              onClick={() => { setToast(null); onNavigate('job_tracker'); }}
              style={{ background: 'none', border: 'none', color: '#93C5FD', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0, flexShrink: 0, whiteSpace: 'nowrap' }}>
              View board
            </button>
          )}
          <button type="button" onClick={() => setToast(null)} aria-label="Dismiss"
            style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}>
            <X size={14} />
          </button>
        </div>
      )}

      {tracker.error && (
        <div role="alert" style={{
          position: 'fixed', left: '50%', bottom: 26, transform: 'translateX(-50%)',
          zIndex: 400, display: 'flex', alignItems: 'center', gap: 10,
          background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA',
          borderRadius: 12, padding: '11px 15px', fontSize: 13, fontWeight: 500,
          boxShadow: '0 12px 32px rgba(15,23,42,0.16)', fontFamily: 'Inter,sans-serif',
        }}>
          <AlertCircle size={15} />{tracker.error}
          <button type="button" onClick={() => tracker.setError('')} aria-label="Dismiss"
            style={{ background: 'none', border: 'none', color: '#B91C1C', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};
